export const setupGlobalErrorHandlers = () => {
    window.onerror = (message, source, lineno, colno, error) => {
      console.error('Global error:', { message, source, lineno, colno, error });
    };
  
    window.onunhandledrejection = (event) => {
      console.error('Unhandled promise rejection:', event.reason);
    };
  };

// User-friendly error message handler for appointment scheduling
export const getUserFriendlyErrorMessage = (error: any, context: string = 'appointment'): string => {
  // Log the full error for debugging
  console.error(`Error in ${context}:`, error);
  
  // Handle structured Athena API error responses
  if (error?.response?.data && typeof error.response.data === 'object') {
    const errorData = error.response.data;
    
    // Check for Athena booking API error format: { hint, error, message }
    if (errorData.hint && errorData.error && errorData.message) {
      // Always use just the main error message, don't include hint
      return errorData.message;
    }
  }
  
  // Extract error message from various possible structures
  const errorMessage = error?.response?.data?.message || 
                      error?.response?.data?.error || 
                      error?.message || 
                      error?.toString() || 
                      'An unexpected error occurred';
  
  // Map technical errors to user-friendly messages
  const errorMappings: Record<string, string> = {
    // Athena booking API specific errors
    'Patient not eligible for booking': 'We cannot book this appointment at this time. Please contact us directly to discuss your eligibility and authorization requirements.',
    'Patient does not have valid authorization to book appointments': 'We cannot book this appointment due to authorization requirements. Please contact us directly to discuss your eligibility.',
    'Patient may need referral authorization or may be a new patient requiring reasonType=\'new\'': 'This appointment requires additional authorization. Please contact us directly to complete the booking process.',
    
    // Appointment-specific errors
    'It is too late to cancel this appointment': 'This appointment cannot be cancelled as it is too close to the scheduled time. Please contact us directly for assistance.',
    'It is too late to reschedule this appointment': 'This appointment cannot be rescheduled as it is too close to the scheduled time. Please contact us directly for assistance.',
    'Appointment not found': 'We could not find this appointment. Please check your details and try again.',
    'Patient not found': 'We could not find your patient record. Please verify your information and try again.',
    'Provider not available': 'The selected provider is not available at this time. Please choose a different time slot.',
    'No available slots': 'No appointment slots are available for the selected criteria. Please try different dates or contact us directly.',
    'Invalid appointment time': 'The selected appointment time is not valid. Please choose a different time slot.',
    'Appointment already exists': 'You already have an appointment scheduled at this time. Please choose a different time slot.',
    'Failed to load available times': 'We are having trouble loading available appointment times. Please try again or contact us directly.',
    'Failed to book appointment': 'We encountered an issue while booking your appointment. Please try again or contact us directly.',
    'Failed to cancel appointment': 'We encountered an issue while cancelling your appointment. Please try again or contact us directly.',
    'Failed to reschedule appointment': 'We encountered an issue while rescheduling your appointment. Please try again or contact us directly.',
    'Failed to update appointment': 'We encountered an issue while updating your appointment. Please try again or contact us directly.',
    'Failed to fetch appointments': 'We are having trouble loading your appointments. Please try again or contact us directly.',
    'No appointment exists for this email': 'No appointments found for this email address. Please verify your email and try again.',
    'We could not verify your details': 'We could not verify your information. Please check your details and try again.',
    
    // Network errors
    'Network Error': 'Please check your internet connection and try again.',
    'Request timeout': 'The request is taking longer than expected. Please try again.',
    'Connection refused': 'We are experiencing technical difficulties. Please try again later.',
    
    // HTTP status codes
    '404': 'The requested information could not be found. Please try again.',
    '500': 'We are experiencing technical difficulties. Please try again later.',
    '503': 'Our service is temporarily unavailable. Please try again later.',
  };
  
  // Check for exact matches first
  for (const [technicalError, userFriendlyMessage] of Object.entries(errorMappings)) {
    if (errorMessage.toLowerCase().includes(technicalError.toLowerCase())) {
      return userFriendlyMessage;
    }
  }
  
  // Check for HTTP status codes
  const statusCode = error?.response?.status;
  if (statusCode) {
    switch (statusCode) {
      case 400:
        return 'The information provided is invalid. Please check your details and try again.';
      case 401:
        return 'Your session has expired. Please refresh the page and try again.';
      case 403:
        return 'You do not have permission to perform this action. Please contact us for assistance.';
      case 404:
        return 'The requested information could not be found. Please try again.';
      case 409:
        return 'There is a conflict with your request. Please try again or contact us for assistance.';
      case 422:
        return 'The information provided is not valid. Please check your details and try again.';
      case 429:
        return 'Too many requests. Please wait a moment and try again.';
      case 500:
        return 'We are experiencing technical difficulties. Please try again later.';
      case 502:
      case 503:
      case 504:
        return 'Our service is temporarily unavailable. Please try again later.';
      default:
        return 'We encountered an unexpected error. Please try again or contact us for assistance.';
    }
  }
  
  // Default fallback message
  return 'We encountered an unexpected error. Please try again or contact us for assistance.';
};